import React, { useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import "../../assets/style/bookingMail.css"
import Axios from "axios"
import { dialog } from '../../utils';

interface GoodsItem {
  Quantity: number;
  Dimensions: string;
  Volume: number;
  LoadMeters: number;
  Weight: number;
}

interface Consignment {
  // Avsändare
  CompanyName: string;
  Street1: string;
  ZipCode: string;
  City: string;
  IsoCountryCode: string;
  ContactName: string;
  ContactPhoneNo: string;

  // Mottagare
  DeliveryCompanyName: string;
  DeliveryStreet1: string;
  DeliveryZipCode: string;
  DeliveryCity: string;
  DeliveryIsoCountryCode: string;
  DeliveryContactName?: string;
  DeliveryContactPhoneNo: string;

  // Upphämtning
  DispatchDate: string;
  OpeningHours: string;
  PickupInstructions: string;
  DeliveryInstructions?: string;

  // Referenser
  ConsignmentNumber: string;
  CustomerNumber: string;
  FreightPayer: string;

  Goods: GoodsItem[];
}

interface TokenPayload {
  id: string;
  json: Consignment;
  [key: string]: unknown;
}

enum Status {
  "UNANSWERED" = 0,
  "DECLINED" = 25,
  "ACCEPTED" = 30,
}

/** Bygger en adressrad för visning. */
function formatAddress(
  street: string,
  city: string,
  zip: string,
  country: string
): string {
  return [street, `${zip} ${city}`.trim(), country].filter(Boolean).join(', ');
}

/** Bygger en adresssträng lämplig för Google Maps. */
function mapsAddress(
  street: string,
  city: string,
  zip: string,
  country: string
): string {
  return [street, zip, city, country].filter(Boolean).join(' ');
}

const numberFormat = new Intl.NumberFormat();

export default function BookingMail() {
  const [comment, setComment] = useState<string>("")
  const [status, setStatus] = useState<number>(Status.UNANSWERED)

  const { consignment, id, action, token, expired } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const action = params.get('a');

    if (!token) return {};

    try {
      const tokenData = jwtDecode<TokenPayload>(token);

      const expired =
        typeof tokenData.exp === 'number'
          ? tokenData.exp * 1000 <= Date.now()
          : false;

      return { consignment: tokenData.json, id: tokenData.id, action, token, expired };
    } catch {
      return {};
    }
  }, []);

  const totals = useMemo(() => {
    return (consignment?.Goods.reduce((prev, cur) => {
      prev.Quantity += cur.Quantity ?? 0
      prev.Weight += cur.Weight ?? 0
      prev.LoadMeters += cur.LoadMeters ?? 0
      prev.Volume += cur.Volume ?? 0
      return prev;
    }, {Quantity: 0, Weight: 0, LoadMeters: 0, Volume: 0}) ?? {}) as Omit<GoodsItem, "Dimensions">
  }, [consignment])

  const bookingResponse = async (action: string, forceConfirm = true) => {
    if (forceConfirm) {
      if (!(await dialog.open({}, { title: `Are you sure you want to ${action.toLowerCase()}?` })))
        return
    }

    Axios.get(`${process.env.REACT_APP_BASE_URL}/central/bookingMail?token=${token}&a=${action}&c=${encodeURIComponent(comment)}`,
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
      })
      .then(({ data }) => {
        setStatus(data.status)
        setComment(data.comment)
      })
      .catch((e) => {
        console.error(e)
      })
  }

  const getBookingStatus = async () => {
    let skeletor: DOMTokenList | undefined;
    skeletor = document.getElementById("skeletor")?.classList;
    skeletor?.add("skeleton-mount")

    Axios.get(`${process.env.REACT_APP_BASE_URL}/central/bookingMail/?token=${token}&a=${action}`,
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
      })
      .then(({ data }: { data: { comment: string, status: number } }) => {
        setComment(data.comment)
        setStatus(data.status)
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => {
        skeletor?.remove("skeleton-mount")
      })
  }

  useEffect(() => {
    if (!action) return

    getBookingStatus();
  }, [action])

  if (!consignment || !id) {
    return (
      <div className="bookingmail justify-center flex-col flex items-center">
        <div className="container container-standalone">
          <h1 className="text-center text-5xl mb-5">Invalid link</h1>

          <div className="card">
            <h2>This link could not be read</h2>
            <p>
              The booking details in this link are missing or damaged. This
              usually happens when the address was only partly copied, or when
              the link was altered on its way to you.
            </p>
            <p>
              Try opening the link directly from the original email. If it still
              does not work, please contact the sender to request a new one.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (expired && status == Status.UNANSWERED) {
    return (
      <div className="bookingmail justify-center flex-col flex items-center">
        <div className="container container-standalone">
          <h1 className='text-center text-5xl mb-5'>Link expired</h1>

          <div className="card">
            <h2>This booking link is no longer valid</h2>
            <p>
              For security reasons, booking links are only valid for a limited
              time. This one has passed its expiry date and can no longer be used
              to confirm or decline the transport.
            </p>
            <p>
              Please contact the sender to request a new link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const pickupAddress = mapsAddress(
    consignment.Street1,
    consignment.City,
    consignment.ZipCode,
    consignment.IsoCountryCode
  );

  const deliveryAddress = mapsAddress(
    consignment.DeliveryStreet1,
    consignment.DeliveryCity,
    consignment.DeliveryZipCode,
    consignment.DeliveryIsoCountryCode
  );

  const mapSrc =
    `https://www.google.com/maps?q=${encodeURIComponent(pickupAddress)}` +
    `+to+${encodeURIComponent(deliveryAddress)}` +
    `&output=embed&travelmode=driving`;

  // Har vi radnivå på godset visar vi det, annars den summerade raden.

  const showTotals = consignment.Goods && consignment.Goods.length > 1;

  return (
    <div className="bookingmail">
      <div className="map-container">
        <iframe src={mapSrc} loading="lazy" title="Route" />
      </div>

      <div className="container">
        <div className="page-header skeleton-f">
          {status === Status.UNANSWERED ?
            <>
              <h1>Transport Booking Confirmation</h1>
              <p>
                Please review the details below and confirm or decline the booking.
              </p>
            </>
            :
            status === Status.ACCEPTED ?
              <h1 className='accepted'>ACCEPTED</h1>
              :
              <h1 className='declined'>DECLINED</h1>
          }

        </div>

        <div className="card grid-2">
          <div>
            <h2>Pickup</h2>
            <div className="info-row">
              <span className="info-label">Company:</span>
              {consignment.CompanyName}
            </div>
            <div className="info-row">
              <span className="info-label">Address:</span>
              {formatAddress(
                consignment.Street1,
                consignment.City,
                consignment.ZipCode,
                consignment.IsoCountryCode
              )}
            </div>
            <div className="info-row">
              <span className="info-label">Contact:</span>
              {consignment.ContactName}
            </div>
            <div className="info-row">
              <span className="info-label">Phone:</span>
              {consignment.ContactPhoneNo}
            </div>
            <div className="info-row">
              <span className="info-label">Date:</span>
              {consignment.DispatchDate}
            </div>
            <div className="info-row">
              <span className="info-label">Opening hours:</span>
              {consignment.OpeningHours}
            </div>
          </div>

          <div>
            <h2>Delivery</h2>
            <div className="info-row">
              <span className="info-label">Company:</span>
              {consignment.DeliveryCompanyName}
            </div>
            <div className="info-row">
              <span className="info-label">Address:</span>
              {formatAddress(
                consignment.DeliveryStreet1,
                consignment.DeliveryCity,
                consignment.DeliveryZipCode,
                consignment.DeliveryIsoCountryCode
              )}
            </div>
            {consignment.DeliveryContactName && (
              <div className="info-row">
                <span className="info-label">Contact:</span>
                {consignment.DeliveryContactName}
              </div>
            )}
            <div className="info-row">
              <span className="info-label">Phone:</span>
              {consignment.DeliveryContactPhoneNo}
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Cargo Details</h2>
          <table>
            <thead>
              <tr>
                <th>Quantity</th>
                <th>Dimensions (LxWxH cm)</th>
                <th>Volume (m³)</th>
                <th>Load Meters</th>
                <th>Weight (kg)</th>
              </tr>
            </thead>
            <tbody>
              {consignment.Goods.map((row, index) => (
                <tr key={index}>
                  <td>{row.Quantity}</td>
                  <td>{row.Dimensions}</td>
                  <td>{numberFormat.format(row.Volume)}</td>
                  <td>{numberFormat.format(row.LoadMeters)}</td>
                  <td>{numberFormat.format(row.Weight)}</td>
                </tr>
              ))}
            </tbody>
            {totals.Quantity && (
              <tfoot>
                <tr>
                  <td>{totals.Quantity}</td>
                  <td></td>
                  <td>{numberFormat.format(totals.Volume)}</td>
                  <td>{numberFormat.format(totals.LoadMeters)}</td>
                  <td>{numberFormat.format(totals.Weight)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="card grid-2">
          <div>
            <h2>Pickup Instructions</h2>
            <div className="instructions">
              {consignment.PickupInstructions || '—'}
            </div>
          </div>

          <div>
            <h2>Delivery Instructions</h2>
            <div className="instructions">
              {consignment.DeliveryInstructions || '—'}
            </div>
          </div>
        </div>

        <div className="card">
          <h2>References</h2>
          <div className="info-row">
            <span className="info-label">Consignment no:</span>
            {consignment.ConsignmentNumber}
          </div>
          <div className="info-row">
            <span className="info-label">Customer no:</span>
            {consignment.CustomerNumber}
          </div>
          <div className="info-row">
            <span className="info-label">Freight payer:</span>
            {consignment.FreightPayer}
          </div>
        </div>

        <div className="card">
          <h2>Comment</h2>
          {status === Status.UNANSWERED ?
            <>
              <textarea placeholder="Add a comment regarding this booking..." value={comment} onChange={(e) => setComment(e.currentTarget.value)} />

              <div className="actions">
                <button className="btn btn-decline" onClick={() => bookingResponse("decline")}>Decline</button>
                <button className="btn btn-confirm" onClick={() => bookingResponse("accept")}>Confirm Booking</button>
              </div>
            </>
            :
            <div>{comment}</div>
          }
        </div>
      </div>
    </div>
  );
}
